import type { LegacyDashboardClient } from '../models/LegacyDashboardClient';
import { normalizeLegacyDashboardSga } from './normalizeLegacyDashboardSga';

function normalizeBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1' || value === 'true') return true;
  if (value === 0 || value === '0' || value === 'false') return false;
  return Boolean(value);
}

function normalizeClient(row: Record<string, unknown>): LegacyDashboardClient | null {
  const legacyTeamId = row.legacy_team_id;
  const name = row.name;
  if (typeof legacyTeamId !== 'number' || typeof name !== 'string') return null;

  return {
    ...(row as LegacyDashboardClient),
    legacy_team_id: legacyTeamId,
    name,
    orders_count: Number(row.orders_count) || 0,
    avg_orders_per_month: Number(row.avg_orders_per_month) || 0,
    is_migrated: normalizeBoolean(row.is_migrated),
    verified: row.verified == null ? row.verified : normalizeBoolean(row.verified),
    is_dropshipping: row.is_dropshipping == null ? row.is_dropshipping : normalizeBoolean(row.is_dropshipping),
    crm_active: row.crm_active == null ? row.crm_active : normalizeBoolean(row.crm_active),
    sga: normalizeLegacyDashboardSga(row.sga),
  };
}

function extractClientArray(value: unknown): LegacyDashboardClient[] | null {
  if (!Array.isArray(value)) return null;
  return value
    .map((item) => (item && typeof item === 'object' ? normalizeClient(item as Record<string, unknown>) : null))
    .filter((item): item is LegacyDashboardClient => item !== null);
}

export function normalizeLegacyDashboardClients(response: unknown): LegacyDashboardClient[] {
  if (!response) return [];

  const direct = extractClientArray(response);
  if (direct) return direct;

  if (typeof response !== 'object') return [];

  const root = response as Record<string, unknown>;

  for (const key of ['clients', 'items', 'rows', 'data'] as const) {
    const nested = root[key];
    const fromKey = extractClientArray(nested);
    if (fromKey) return fromKey;

    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const inner = nested as Record<string, unknown>;
      for (const innerKey of ['clients', 'items', 'rows', 'data'] as const) {
        const fromInner = extractClientArray(inner[innerKey]);
        if (fromInner) return fromInner;
      }
    }
  }

  return [];
}
